import {
  Button,
  Flex,
  FormControl,
  Image,
  List,
  ListItem,
  Box,
  VStack,
  Text,
  Input,
  FormLabel,
} from "@chakra-ui/react";
import FullNameSVG from "@root/assets/images/fullName.svg";
import PasswordSVG from "@root/assets/images/password.svg";

import {
  ErrorMessageText,
  StyledInputField,
} from "@root/commonStyles/commonStyle";
import { PasswordInput } from "@root/components/common/passwordInput/passwordInput";
import { Formik } from "formik";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AllSetTopHeading,
  AllSetHeading,
  AllSetSubHeading,
  ResetPasswordRequirements,
  ForgotPasswordForm,
  FormContainer,
  StyledDoubledText,
  StyledDoubledEmailText,
} from "./styles";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { fullSignup } from "@root/store/thunks";
import { ROUTE_NAMES } from "@root/constants";
import { BrandedLogo } from "@root/components/branding";
import { RightArrowIcon } from "@root/assets/icons/rightArrow";

interface AllSetValues {
  fullname: string;
  password: string;
  rePassword: string;
}
interface DisplayRequirementsProps {
  requirements: string;
}

export const AllSet: React.FC = () => {
  const { t } = useTranslation("common");
  const initialValues: AllSetValues = {
    fullname: "",
    password: "",
    rePassword: "",
  };

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams?.get("token");
  const email = searchParams?.get("email");
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const validate = (values: AllSetValues) => {
    const errors: Partial<AllSetValues> = {};

    if (!values.password) {
      errors.password = t("login.requiredPassword");
    } else if (values.password.length < 8) {
      errors.password = t("login.passwordValidation");
    }
    if (!values.rePassword) {
      errors.rePassword = t("resetPassword.confirmRequiredPassword");
    } else if (values.rePassword !== values.password) {
      errors.rePassword = t("resetPassword.samePasword");
    }
    return errors;
  };

  const navigate = useNavigate();
  const onSuccess = (fullName: any) => {
    setLoading(true);
    navigate(ROUTE_NAMES.DASHBOARD);
  };
  const onError = () => {
    setLoading(false);
  };
  const onSubmitHandler = (values: any) => {
    setLoading(true);
    try {
      const payload = {
        fullname: values.fullname,
        password: values.password,
      };
      dispatch(
        fullSignup({
          payload,
          token,
          onSuccess: () => onSuccess(values?.fullname),
          onError,
        })
      );
    } catch (error) {
      console.log(error);
    }
  };

  const DisplayRequirements: React.FC<DisplayRequirementsProps> = ({
    requirements,
  }) => {
    const lines = requirements.split("\n");

    return (
      <Box
        fontSize="14px"
        alignItems="flex-start"
        justifyItems="start"
        fontWeight="400"
        color={"theme.gray.500"}
      >
        {t("translate.requirements")}
        <ResetPasswordRequirements>
          <List spacing={1} alignItems="flex-start" justifyItems="start">
            {lines.map((line, index) => {
              if (line.startsWith("• ")) {
                return <ListItem key={index}>{line.trim()}</ListItem>;
              } else if (line.startsWith("  • ")) {
                return <ListItem key={index}>{line.trim()}</ListItem>;
              } else {
                return null;
              }
            })}
          </List>
        </ResetPasswordRequirements>
      </Box>
    );
  };

  return (
    <FormContainer>
      <AllSetTopHeading>
        <Box mb={"15px"}>
          <BrandedLogo variant="full" />
        </Box>
      </AllSetTopHeading>

      <StyledDoubledText>
        <AllSetHeading>{t("allSet.subTitle")}</AllSetHeading>
      </StyledDoubledText>
      <AllSetSubHeading>{t("allSet.subTitle1")}</AllSetSubHeading>

      <Formik
        initialValues={initialValues}
        validate={validate}
        onSubmit={onSubmitHandler}
      >
        {({ handleSubmit, errors, touched, values, handleChange }) => (
          <ForgotPasswordForm
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <VStack mt="22px" spacing="24px" align="flex-start">
              {/* Email Tag */}
              <StyledDoubledEmailText>
                <Text fontWeight={400} color="gray.grayText" align="left">
                  {t("allSet.email")}
                </Text>
                <Text color="purple.500">
                  {email ? email : "user@email.com"}
                </Text>
              </StyledDoubledEmailText>
              {/* Input Fields */}
              <Box
                width="100%"
                display="flex"
                flexDirection="column"
                mx="auto"
                gap="18px"
              >
                {/* I would love  */}
                <Flex
                  flexDirection="row"
                  gap="12px"
                  alignItems="flex-start"
                  justifyContent="flex-start"
                >
                  <Image src={FullNameSVG} alt="FullNameSVG" mt="3px" />
                  <FormLabel fontSize="18px" color="inverse.black" mb={0}>
                    {t("allSet.fullNameLabel")}
                  </FormLabel>
                </Flex>

                <FormControl
                  isInvalid={!!errors.password && touched.password}
                  maxW="500px"
                  width="100%"
                  justifyContent="center"
                  alignItems="center"
                >
                  <StyledInputField
                    as={Input}
                    borderColor={values.fullname ? "purple.500" : "gray.200"}
                    onChange={handleChange}
                    value={values.fullname}
                    id="fullname"
                    name="fullname"
                    required
                    placeholder={t("allSet.fullNamePlaceholder")}
                  />
                  <ErrorMessageText name="fullname" component="div" />
                </FormControl>

                {/* Account Secure */}
                <Flex
                  flexDirection="row"
                  gap="12px"
                  alignItems="flex-start"
                  justifyContent="flex-start"
                >
                  <Image src={PasswordSVG} alt="PasswordSVG" mt="3px" />
                  <FormLabel fontSize="18px" color="inverse.black" mb={0}>
                    {t("allSet.passwordLabel")}
                  </FormLabel>
                </Flex>

                <FormControl
                  isInvalid={!!errors.password && touched.password}
                  maxW="500px"
                  width="100%"
                >
                  <PasswordInput
                    onChange={handleChange}
                    value={values.password}
                    id="password"
                    name="password"
                    required
                    placeholder={t("resetPassword.password")}
                  />
                  <ErrorMessageText name="password" component="div" />
                </FormControl>

                <FormControl
                  isInvalid={!!errors.rePassword && touched.rePassword}
                  maxW="500px"
                  width="100%"
                >
                  <PasswordInput
                    onChange={handleChange}
                    value={values.rePassword}
                    id="rePassword"
                    name="rePassword"
                    required
                    placeholder={t("resetPassword.confirmPassword")}
                  />
                  <ErrorMessageText name="rePassword" component="div" />
                </FormControl>
                <AllSetSubHeading></AllSetSubHeading>
              </Box>
            </VStack>
            <Text fontWeight={400} color="gray.grayText" align="left">
              {t("allSet.changePasswordMsg")}
            </Text>

            <Flex justifyContent="center" my="26px">
              <Button
                type="submit"
                colorScheme="purple"
                size="md"
                fontWeight={500}
                isLoading={loading}
                fontSize="16px"
                background="purple.purpleGradient"
                borderRadius="16px"
                rightIcon={<RightArrowIcon />}
              >
                {t("allSet.begin")}
              </Button>
            </Flex>
          </ForgotPasswordForm>
        )}
      </Formik>
    </FormContainer>
  );
};
